<template>
  <div class="AppSidebar">
    <!-- WHEN COLLAPSED, THE HEADER ITSELF -- MONOGRAM INCLUDED -- IS AN EXPAND CONTROL.
         The button below is still the primary affordance, but it is deliberately no longer
         the ONLY one. A control whose sole affordance can be laid out into unreachability
         is a one-way door, and that is exactly what happened here: the header is sized for
         the 250px rail, so in the 75px rail the button was pushed outside it and painted
         under .layout-main -- collapse worked, expand did not. Binding the header too means
         the state stays reversible even if the geometry regresses again.
         Scoped to the collapsed state, so the expanded logo keeps the inert behaviour it
         has always had. -->
    <div
      class="AppSidebar__header"
      :class="{ 'AppSidebar__header--clickable': isSidebarMinified }"
      :title="isSidebarMinified ? 'Expand sidebar' : null"
      @click="onHeaderClick"
    >
      <img src="/img/cft-logo-full-white.svg" alt="Capital Flow Tech" class="AppSidebar__logo AppSidebar__logo--full" />
      <img src="/img/cft-monogram-white.svg" alt="CFT" class="AppSidebar__logo AppSidebar__logo--mono" />
      <!-- .stop: without it the click bubbles to the header as well, both handlers fire,
           and the two toggles cancel out -- the button would silently do nothing. -->
      <button
        class="btn btn-text AppSidebar__menu-toggle"
        :aria-label="isSidebarMinified ? 'Expand sidebar' : 'Collapse sidebar'"
        @click.stop="onToggleSidebarMinified"
      >
        <i v-if="isSidebarMinified" class="bi bi-list" />
        <i v-if="!isSidebarMinified" class="bi bi-arrow-left-short" />
      </button>
    </div>

    <ul class="AppSidebar__menu">
      <li>
        <router-link class="AppSidebar__item" :to="{ name: 'dashboard' }" title="Dashboard">
          <div class="AppSidebar__icon">
            <i class="bi bi-speedometer2" />
          </div>
          <div class="AppSidebar__text">
            Dashboard
          </div>
        </router-link>
      </li>

      <li v-if="!isAuthAdmin || !isAuthAgent || isAuthImpersonate">
        <router-link class="AppSidebar__item" :to="{ name: 'allocations' }" title="Allocations">
          <div class="AppSidebar__icon">
            <i class="bi bi-wallet" />
          </div>
          <div class="AppSidebar__text">
            Allocations
          </div>
        </router-link>
      </li>

      <li v-if="(!isAuthAdmin && !isAuthAgent) || isAuthImpersonate">
        <router-link class="AppSidebar__item" :to="{ name: 'transfers' }" title="Transfers">
          <div class="AppSidebar__icon">
            <i class="bi bi-arrow-left-right" />
          </div>
          <div class="AppSidebar__text">
            Transfers
          </div>
        </router-link>
      </li>

      <li v-if="(!isAuthAdmin && !isAuthAgent) || isAuthImpersonate" class="AppSidebar__admin">
        <router-link class="AppSidebar__item" :to="{ name: 'aums' }" title="Monthly Net Value">
          <div class="AppSidebar__icon">
            <i class="bi bi-bar-chart" />
          </div>
          <div class="AppSidebar__text">
            Monthly Net Value
          </div>
        </router-link>
      </li>

      <li v-if="(!isAuthAdmin && !isAuthAgent) || isAuthImpersonate">
        <router-link class="AppSidebar__item" :to="{ name: 'invoices' }" title="Invoices">
          <div class="AppSidebar__icon">
            <i class="bi bi-receipt-cutoff" />
          </div>
          <div class="AppSidebar__text">
            Invoices
          </div>
        </router-link>
      </li>

      <li>
        <router-link class="AppSidebar__item" :to="{ name: 'account' }" title="My account">
          <div class="AppSidebar__icon">
            <i class="bi bi-person" />
          </div>
          <div class="AppSidebar__text">
            My account
          </div>
        </router-link>
      </li>

      <li v-if="isAuthAdmin || isAuthAgent" class="AppSidebar__admin">
        <router-link class="AppSidebar__item" :to="{ name: 'accounting' }" title="Accounting">
          <div class="AppSidebar__icon">
            <i class="bi bi-currency-dollar" />
          </div>
          <div class="AppSidebar__text">
            Accounting
          </div>
        </router-link>
      </li>

      <li v-if="isAuthAdmin || isAuthAgent" class="AppSidebar__admin">
        <router-link class="AppSidebar__item" :to="{ name: 'clients' }" title="Clients">
          <div class="AppSidebar__icon">
            <i class="bi bi-people" />
          </div>
          <div class="AppSidebar__text">
            Clients
          </div>
        </router-link>
      </li>

      <!-- "Introducer" is the client-facing wording for type:'agent'. DISPLAY ONLY: the
           database keeps type:'agent' untouched, and so do the route (name:'agents',
           path:'/agents'), the store module, the service and the CSS classes. -->
      <li v-if="isAuthAdmin" class="AppSidebar__admin">
        <router-link class="AppSidebar__item" :to="{ name: 'agents' }" title="Introducers">
          <div class="AppSidebar__icon">
            <i class="bi bi-bookmarks" />
          </div>
          <div class="AppSidebar__text">
            Introducers
          </div>
        </router-link>
      </li>

      <!-- Two entries, two different objects: "Introducers" is WHO THEY ARE (the people
           list, AgentsView), "Commissions" is WHAT THEY ARE OWED (the ledger,
           CommissionsView). Admin only in this phase -- there is no introducer-facing view
           until the Introducer Agreement is signed. The path stays '/introducers/' because
           breaking bookmarks buys nothing. -->
      <li v-if="isAuthAdmin" class="AppSidebar__admin">
        <router-link class="AppSidebar__item" :to="{ name: 'commissions' }" title="Commissions">
          <div class="AppSidebar__icon">
            <i class="bi bi-percent" />
          </div>
          <div class="AppSidebar__text">
            Commissions
          </div>
        </router-link>
      </li>

      <li v-if="isAuthAdmin" class="AppSidebar__admin">
        <router-link class="AppSidebar__item" :to="{ name: 'settings' }" title="Settings">
          <div class="AppSidebar__icon">
            <i class="bi bi-gear" />
          </div>
          <div class="AppSidebar__text">
            Settings
          </div>
        </router-link>
      </li>
    </ul>
  </div>
</template>

<script>
  import { mapState, mapActions, mapGetters } from 'vuex'

  export default {
    computed: {
      ...mapState('app', [
        'isSidebarMinified'
      ]),

       ...mapState('auth', [
        'authenticated',
        'impersonated'
      ]),

      ...mapGetters('auth', [
        'isAuthAdmin',
        'isAuthAgent',
        'isAuthImpersonate'
      ])
    },

    methods: {
      ...mapActions('app', [
        'toggleSidebarMinified'
      ]),

      onToggleSidebarMinified() {
        this.toggleSidebarMinified()
      },

      /* The collapsed rail's header is a second, independent way back. Only when
         collapsed: expanded, the logo stays inert exactly as before, so this adds a way
         out without altering anything that already worked. */
      onHeaderClick() {
        if (this.isSidebarMinified) {
          this.toggleSidebarMinified()
        }
      }
    }
  }
</script>

<style lang="sass" scoped>
  @import '@/sass/styles.vars.all.sass'

  .AppSidebar
    width: $sidebar-width
    height: 100vh
    transition: width .25s
    color: rgba(white, .82)
    display: flex
    flex-direction: column
    background: linear-gradient(180deg, #0f1537 0%, #1a2456 55%, #202c73 100%)
    border-right: 1px solid rgba(white, .06)

    a
      text-decoration: none
      color: currentColor

  .AppSidebar__header
    height: $header-height
    display: flex
    align-items: center
    justify-content: space-between
    padding: 0 $spacer-3
    border-bottom: 1px solid rgba(white, .08)

  .AppSidebar__logo
    transition: opacity .25s

  .AppSidebar__logo--full
    height: 48px
    width: auto

  .AppSidebar__logo--mono
    height: 42px
    width: auto
    display: none

  .AppSidebar__menu-toggle
    display: none
    color: rgba(white, .8)
    background: none
    border: none

    i
      font-size: 1.5rem

    @include media-breakpoint-up(sm)
      display: block

  .AppSidebar__menu
    padding: 0
    margin-top: .75rem
    flex: 1

  .AppSidebar__item
    display: flex
    align-items: center
    border-left: 3px solid transparent
    color: rgba(white, .8)
    transition: .2s all

    &:hover
      background: rgba(white, .06)
      color: white

  .AppSidebar__icon
    padding: .6em 1em
    font-size: 1.4rem
    margin-left: 1px

  .AppSidebar__text
    font-size: 13px
    letter-spacing: .2px
    transition: opacity .25s
    white-space: nowrap

  .AppSidebar .active
    &
      border-color: $tertiary
      background: linear-gradient(90deg, rgba($tertiary, .18) 0%, rgba(white, .06) 100%)
      color: white

    .AppSidebar__icon
      color: $tertiary

    .AppSidebar__text
      color: white
      font-weight: 600

  .AppSidebar__admin
    .AppSidebar__icon
      color: $tertiary

    & .active
      .AppSidebar__icon
        color: $tertiary

  // Presented as clickable only while collapsed; the class is bound to isSidebarMinified.
  .AppSidebar__header--clickable
    cursor: pointer

  // Structural guard, independent of the numbers below. Flex items default to
  // min-width:auto, which refuses to shrink past min-content and so overflows the rail
  // SILENTLY -- the overflowing button kept its full size and simply left the sidebar.
  // min-width:0 makes a future overflow shrink visibly instead of escaping unnoticed.
  .AppSidebar__logo,
  .AppSidebar__menu-toggle
    min-width: 0

  @include media-breakpoint-up(sm)
    .sidebar-is-minified
      .AppSidebar
        width: $sidebar-width-minified

      .AppSidebar__text
        opacity: 0

      .AppSidebar__logo--full
        display: none

      // THE GEOMETRY THAT MADE THIS A ONE-WAY DOOR, and the fix.
      // The header is laid out for the 250px rail: padding 0 $spacer-3 leaves a 43px
      // content box inside a 75px rail, while the monogram alone renders ~65px wide
      // (viewBox 1117.2x723.2 at height 42px) and the button ~48px. 113px of content in
      // 43px, with justify-content:space-between, pushed the button clean outside the
      // rail where .layout-main painted over it.
      // Drop the padding, centre the row, and size both so they genuinely fit:
      // 26px x 1.545 = 40px monogram + 20px icon + 4px gap = 64px inside 75px.
      .AppSidebar__header
        padding: 0
        justify-content: center
        gap: .25rem

      .AppSidebar__logo--mono
        display: block
        height: 26px

      .AppSidebar__menu-toggle
        padding: 0

        i
          font-size: 1.25rem
</style>
